import { combineReducers, Reducer, ReducersMapObject } from "redux";

import { coreModules, NAME as CORE_NAME } from "@multiweb/reactCore";
import { customizationModule } from "@multiweb/reactCustomization";
import { ApplicationModule, ApplicationStatefulModule, IAppState, ICoreState } from "@multiweb/types/react";
import { aggregationRowModule } from "../aggregationRow";
import { linkButtonModule } from "../components/LinkButton";
import { reportingScalarModule } from "../components/ReportingScalar";
import { controlsModule } from "../controls";
import { formsModule } from "../forms";
import { framesModule } from "../frames";
import { menuModule } from "../menu";
import { rightsModule } from "../rights";
import { workflowEditorModule } from "../workflowEditor";

interface MaybeStatefulModule extends ApplicationModule<string> {
    reducer?: Reducer;
}

/**
 * Přefiltruje zadané moduly tak, že výstupem jsou pouze moduly obsahující reducer.
 * Tato funkcionalita nemůže být součástí funkce combineModuleReducers kvůli správnému přetypování výsledku.
 *
 * @param modules Moduly k filtrování.
 */
const filterReducerModules = (modules: Array<ApplicationModule<string>>): Array<ApplicationStatefulModule<string, Reducer>> => modules
    .filter((module: MaybeStatefulModule) => module && !!module.reducer) as Array<ApplicationStatefulModule<string, Reducer>>;

/**
 * Sesbírá a zkombinuje reducery ze zadaných modulů. Pokud modul nemá reducer, je přeskočen. Pokud reducer má, je zapojen pod jménem modulu.
 *
 * @param modules Pole modulů, jejichž reducery se mají zapojit.
 */
const combineModuleReducers = <T>(modules: Array<ApplicationModule<string>>): Reducer<T> => combineReducers(
    filterReducerModules(modules)
        .reduce((moduleReducers, module) => Object.assign({}, moduleReducers, { [module.NAME]: module.reducer }), {}) as ReducersMapObject,
);

/**
 * Vrací hlavní reducer aplikace. Do samostatného podstrumu zapojuje core moduly, dále jedonotlivé business moduly a do dalšího samostatného podstromu custom moduly.
 */
export const getRootReducer = (): Reducer<IAppState> => (
    combineReducers({
        [CORE_NAME]: combineModuleReducers<ICoreState>(coreModules),
        [customizationModule.NAME]: customizationModule.getReducer(),
        [menuModule.NAME]: menuModule.reducer,
        [aggregationRowModule.NAME]: aggregationRowModule.reducer,
        [workflowEditorModule.NAME]: workflowEditorModule.reducer,
        [controlsModule.NAME]: controlsModule.reducer,
        [framesModule.NAME]: framesModule.reducer,
        [formsModule.NAME]: formsModule.reducer,
        [rightsModule.NAME]: rightsModule.reducer,
        [reportingScalarModule.NAME]: reportingScalarModule.reducer,
        [linkButtonModule.NAME]: linkButtonModule.reducer,
    })
);
